import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { Admin, AdminInsertRequest, AdminLocationEmails, AdminUpdateRequest, PasswordRequirements } from './admin';
import { handleRequest } from '../quipli-service-client';
import { AdminParsers } from './admin-parsers';
import AdminJson = AdminParsers.AdminJson;
import { Location } from '../location/location';
import { LocationParsers } from '../location/location-parsers';

export const useAdminApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function getMultipleAdminsByIds(adminIds: number[]): Promise<Admin[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/admins/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: adminIds,
    };
    const adminJsons = await handleRequest<AdminJson[]>(config);
    return adminJsons.map(AdminParsers.toAdmin);
  }

  async function updateAdminLoggedProfile(request: Omit<AdminUpdateRequest, 'adminId'>): Promise<Admin> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/admins/admin/profile`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<AdminParsers.AdminJson>(config);
    return AdminParsers.toAdmin(result);
  }

  async function updateUserAdmin(adminId: number, request: AdminUpdateRequest): Promise<Admin> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<AdminParsers.AdminJson>(config);
    return AdminParsers.toAdmin(result);
  }

  async function createUserAdmin(request: AdminInsertRequest): Promise<Admin> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/admins/`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<AdminParsers.AdminJson>(config);
    return AdminParsers.toAdmin(result);
  }

  async function deleteAdmin(adminId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<void>(config);
  }

  async function getAdminsList(includeDeleted: boolean = false): Promise<Admin[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/admins?includeDeleted=${includeDeleted}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<AdminParsers.AdminJson[]>(config);
    return result.map(AdminParsers.toAdmin);
  }

  async function getUserAdmin(adminId: number): Promise<Admin> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<AdminParsers.AdminJson>(config);
    return AdminParsers.toAdmin(result);
  }

  async function getAdminLoggedProfile(): Promise<Admin> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/admins/admin/profile`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<AdminParsers.AdminJson>(config);
    return AdminParsers.toAdmin(result);
  }

  async function getUserAdminPermissions(adminId: number): Promise<string[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}/permissions`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<string[]>(config);
  }

  async function updateUserAdminPermissions(adminId: number, permissions: string[]): Promise<string[]> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}/permissions`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        permissions,
      },
    };

    return handleRequest<string[]>(config);
  }

  async function getUserAdminEmailNotifications(adminId: number): Promise<AdminLocationEmails> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/emails/users/${adminId}/email-permissions`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<AdminLocationEmails>(config);
  }

  async function updateUserAdminEmailNotifications(
    adminId: number,
    userLocationEmailTemplates: AdminLocationEmails,
  ): Promise<AdminLocationEmails> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/emails/users/${adminId}/email-permissions`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        userLocationEmailTemplates,
      },
    };

    return handleRequest<AdminLocationEmails>(config);
  }

  async function listAdminLocations(adminId: number): Promise<Location[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}/locations`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<LocationParsers.LocationJson[]>(config);
    return result.map(LocationParsers.toLocation);
  }

  async function updateAdminUserLocations(adminId: number, locations: number[]): Promise<number[]> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/admins/${adminId}/locations`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        locations,
      },
    };

    return handleRequest<number[]>(config);
  }

  async function getPasswordRequirements(): Promise<PasswordRequirements> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/admins/passwordRequirements`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const json = await handleRequest<AdminParsers.PasswordRequirementsJson>(config);
    return AdminParsers.toPasswordRequirements(json);
  }

  return {
    getAdminsList,
    getMultipleAdminsByIds,
    updateAdminLoggedProfile,
    updateUserAdmin,
    createUserAdmin,
    deleteAdmin,
    getUserAdmin,
    getAdminLoggedProfile,
    getUserAdminPermissions,
    updateUserAdminPermissions,
    getUserAdminEmailNotifications,
    updateUserAdminEmailNotifications,
    listAdminLocations,
    updateAdminUserLocations,
    getPasswordRequirements,
  };
};
